import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Loader2, Pencil, Plus, Star, Trash2, DollarSign } from 'lucide-react';
import api from '../lib/api';

const ERP_MODULES = [
  { id: 'sales', label: 'Sales & POS' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'clinical', label: 'Clinical' },
  { id: 'customer_engagement', label: 'Client Engagement' },
  { id: 'ai_tools', label: 'AI Tools' },
  { id: 'procurement', label: 'Procurement' },
  { id: 'finance', label: 'Finance' },
  { id: 'hr', label: 'HR & Payroll' },
  { id: 'crm', label: 'CRM & Billing' },
  { id: 'admin', label: 'Admin' },
];

const emptyForm = {
  name: '', price: '', billing_period: 'month', description: '',
  features: '', is_highlighted: false, cta_text: 'Get started',
  annual_discount_pct: '20', signup_url: '', enabled_modules: [],
};

const toFormState = (p) => ({
  name: p.name, price: String(p.price), billing_period: p.billing_period,
  description: p.description, features: (p.features || []).join('\n'),
  is_highlighted: !!p.is_highlighted, cta_text: p.cta_text,
  annual_discount_pct: String(p.annual_discount_pct ?? 20),
  signup_url: p.signup_url || '',
  enabled_modules: p.enabled_modules || [],
});

const PlanForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial ? toFormState(initial) : emptyForm);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  const save = async () => {
    if (!form.name.trim()) { toast.error('Plan name is required'); return; }
    if (form.price === '' || Number(form.price) < 0) { toast.error('Enter a valid price'); return; }
    const payload = {
      name: form.name.trim(),
      price: Number(form.price),
      billing_period: form.billing_period.trim() || 'month',
      description: form.description.trim(),
      features: form.features.split('\n').map((f) => f.trim()).filter(Boolean),
      is_highlighted: form.is_highlighted,
      cta_text: form.cta_text.trim() || 'Get started',
      annual_discount_pct: Math.min(100, Math.max(0, Number(form.annual_discount_pct) || 20)),
      signup_url: form.signup_url.trim(),
      enabled_modules: form.enabled_modules,
    };
    setBusy(true);
    try {
      if (isEdit) await api.put(`/platform/plans/${initial.plan_id}`, payload);
      else await api.post('/platform/plans', payload);
      toast.success(isEdit ? 'Plan updated' : 'Plan created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save plan'); }
    finally { setBusy(false); }
  };

  return (
    <div className="pc-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pc-modal" style={{ maxWidth: 560 }} data-testid="pc-plan-form">
        <h3>{isEdit ? `Edit ${initial.name}` : 'New plan'}</h3>
        <p className="pc-sub" style={{ marginBottom: '.9rem' }}>
          Shown on the public marketing pricing page as soon as you save.
        </p>
        <div className="pc-row">
          <div>
            <label className="pc-field">Plan name</label>
            <input className="pc-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Professional" data-testid="pc-plan-name" />
          </div>
          <div>
            <label className="pc-field">Price</label>
            <input className="pc-input" type="number" min="0" step="0.01" value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="89" data-testid="pc-plan-price" />
          </div>
        </div>
        <div className="pc-row" style={{ marginTop: '.6rem' }}>
          <div>
            <label className="pc-field">Billing period label</label>
            <input className="pc-input" value={form.billing_period}
              onChange={(e) => setForm((f) => ({ ...f, billing_period: e.target.value }))}
              placeholder="month" data-testid="pc-plan-period" />
          </div>
          <div>
            <label className="pc-field">CTA button text</label>
            <input className="pc-input" value={form.cta_text}
              onChange={(e) => setForm((f) => ({ ...f, cta_text: e.target.value }))}
              placeholder="Start free 14-day trial" data-testid="pc-plan-cta" />
          </div>
          <div>
            <label className="pc-field">Annual discount %</label>
            <input className="pc-input" type="number" min="0" max="100" value={form.annual_discount_pct}
              onChange={(e) => setForm((f) => ({ ...f, annual_discount_pct: e.target.value }))}
              placeholder="20" data-testid="pc-plan-annual-discount" />
          </div>
        </div>
        <label className="pc-field" style={{ marginTop: '.6rem' }}>Short description</label>
        <input className="pc-input" value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="For independent pharmacies just getting started" data-testid="pc-plan-description" />
        <label className="pc-field" style={{ marginTop: '.6rem' }}>Features (one per line)</label>
        <textarea className="pc-textarea" rows={6} value={form.features}
          onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))}
          placeholder={'Smart POS · Hold & Recall\nInventory · Batch tracking'} data-testid="pc-plan-features" />
        <label className="pc-field" style={{ marginTop: '.6rem' }}>Sign-up URL <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(leave blank for default signup page)</span></label>
        <input className="pc-input" value={form.signup_url}
          onChange={(e) => setForm((f) => ({ ...f, signup_url: e.target.value }))}
          placeholder="/signup/pharmacy?plan=essentials  or  https://calendly.com/..." data-testid="pc-plan-signup-url" />
        <label className="pc-field" style={{ marginTop: '.6rem' }}>ERP modules included</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.3rem .8rem' }}>
          {ERP_MODULES.map((m) => (
            <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontSize: '.78rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={(form.enabled_modules || []).includes(m.id)}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  enabled_modules: e.target.checked
                    ? [...(f.enabled_modules || []), m.id]
                    : (f.enabled_modules || []).filter((x) => x !== m.id),
                }))}
                data-testid={`pc-plan-module-${m.id}`}
              />
              {m.label}
            </label>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: '.75rem', fontSize: '.8rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_highlighted}
            onChange={(e) => setForm((f) => ({ ...f, is_highlighted: e.target.checked }))}
            data-testid="pc-plan-highlighted" />
          Mark as "Most popular"
        </label>
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1.1rem' }}>
          <button type="button" className="pc-btn" onClick={onClose} data-testid="pc-plan-cancel">Cancel</button>
          <button type="button" className="pc-btn pc-btn-primary" disabled={busy} onClick={save} data-testid="pc-plan-save">
            {busy ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Exchange rate card (shared between Plans and Pricing screens) ── */
export const ExchangeRateCard = () => {
  const [rate, setRate] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/platform/settings/exchange-rate').then((r) => {
      setRate(r.data.usd_ghs_rate);
      setDraft(String(r.data.usd_ghs_rate));
    }).catch(() => {});
  }, []);

  const save = async () => {
    const val = Number(draft);
    if (!val || val <= 0) { toast.error('Enter a valid rate'); return; }
    setBusy(true);
    try {
      const { data } = await api.put('/platform/settings/exchange-rate', { rate: val });
      setRate(data.usd_ghs_rate);
      toast.success(`Exchange rate updated to ${data.usd_ghs_rate}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save rate'); }
    finally { setBusy(false); }
  };

  if (rate === null) return null;
  const changed = Number(draft) !== rate;

  return (
    <div className="pc-card" style={{ marginBottom: '1rem' }} data-testid="pc-exchange-rate-card">
      <div className="pc-card-h" style={{ gap: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <DollarSign className="h-3.5 w-3.5" style={{ color: '#15a6a6' }} />
          <h2 style={{ whiteSpace: 'nowrap' }}>USD → GHS rate</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <span className="pc-muted" style={{ whiteSpace: 'nowrap' }}>1 USD =</span>
          <input
            className="pc-input"
            type="number"
            min="0.01"
            step="0.01"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ width: '90px' }}
            data-testid="pc-exchange-rate-input"
          />
          <span className="pc-muted">GHS</span>
          {changed && (
            <button type="button" className="pc-btn pc-btn-primary" disabled={busy} onClick={save}
              data-testid="pc-exchange-rate-save">
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const PlatformPlans = () => {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get('/platform/plans').then((r) => setPlans(r.data.plans))
      .catch(() => toast.error('Could not load plans'));
  }, []);
  useEffect(load, [load]);

  const toggleActive = async (p) => {
    try {
      await api.put(`/platform/plans/${p.plan_id}`, { active: !p.active });
      toast.success(p.active ? `${p.name} hidden from the marketing site` : `${p.name} is now live`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update plan'); }
  };

  const move = async (index, dir) => {
    const next = [...plans];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPlans(next);
    try {
      await api.post('/platform/plans/reorder', { order: next.map((p) => p.plan_id) });
    } catch (e) { toast.error('Could not save order'); load(); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/platform/plans/${p.plan_id}`);
      toast.success('Plan deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete plan'); }
  };

  return (
    <div data-testid="pc-plans">
      <ExchangeRateCard />
      <div className="pc-page-head">
        <div>
          <h1 className="pc-h1">Plans</h1>
          <p className="pc-sub">What prospects see on the marketing pricing page — edits go live immediately, no deploy needed.</p>
        </div>
        <button type="button" className="pc-btn pc-btn-primary" onClick={() => setCreating(true)} data-testid="pc-plan-add-btn">
          <Plus className="h-3.5 w-3.5 inline" /> Add plan
        </button>
      </div>

      {!plans ? (
        <div className="pc-muted" style={{ marginTop: '1rem' }}><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : (
        <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
          <table className="pc-table">
            <thead>
              <tr><th>Order</th><th>Plan</th><th className="pc-num">Price</th><th>Billing</th><th className="pc-num">Annual</th><th>Popular</th><th>Status</th><th className="pc-num">Actions</th></tr>
            </thead>
            <tbody>
              {plans.map((p, i) => (
                <tr key={p.plan_id} data-testid={`pc-plan-row-${p.plan_id}`}>
                  <td>
                    <div style={{ display: 'flex', gap: '.2rem' }}>
                      <button type="button" className="pc-btn" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`pc-plan-up-${p.plan_id}`}>
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button type="button" className="pc-btn" disabled={i === plans.length - 1} onClick={() => move(i, 1)} data-testid={`pc-plan-down-${p.plan_id}`}>
                        <ArrowDown className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                  <td><b style={{ color: '#fff' }}>{p.name}</b><div className="pc-muted">{p.description}</div></td>
                  <td className="pc-num pc-mono">${Number(p.price).toLocaleString()}</td>
                  <td>/ {p.billing_period}</td>
                  <td className="pc-num pc-mono">{p.annual_discount_pct ?? 20}% off</td>
                  <td>{p.is_highlighted && <Star className="h-3.5 w-3.5" style={{ color: '#ffcf72' }} />}</td>
                  <td>
                    <button type="button" className={`pc-pill pc-pill-${p.active ? 'active' : 'suspended'}`}
                      onClick={() => toggleActive(p)} style={{ cursor: 'pointer' }} data-testid={`pc-plan-toggle-${p.plan_id}`}>
                      {p.active ? 'Live' : 'Hidden'}
                    </button>
                  </td>
                  <td className="pc-num">
                    <div style={{ display: 'flex', gap: '.35rem', justifyContent: 'flex-end' }}>
                      <button type="button" className="pc-btn" onClick={() => setEditing(p)} data-testid={`pc-plan-edit-${p.plan_id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" className="pc-btn pc-btn-danger" onClick={() => remove(p)} data-testid={`pc-plan-delete-${p.plan_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr><td colSpan={8} className="pc-empty" data-testid="pc-plans-empty">No plans yet — add one to populate the marketing pricing page.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && <PlanForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <PlanForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
